import { useState, useEffect } from 'react';

export interface DurationInputProps {
  value?: number; // Duration in minutes
  onChange: (duration: number | undefined) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const PRESET_DURATIONS = [
  { label: '30 min', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: 'All day', value: 480 }, // 8 hours
];

export const DurationInput = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  className = '',
}: DurationInputProps) => {
  const [customValue, setCustomValue] = useState<string>(value?.toString() || '');
  const [isCustom, setIsCustom] = useState<boolean>(
    value !== undefined && !PRESET_DURATIONS.some(preset => preset.value === value)
  );

  // Update local state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setCustomValue(value.toString());
      setIsCustom(!PRESET_DURATIONS.some(preset => preset.value === value));
    } else {
      setCustomValue('');
      setIsCustom(false);
    }
  }, [value]);

  const handlePresetClick = (presetValue: number) => {
    setIsCustom(false);
    setCustomValue(presetValue.toString());
    onChange(presetValue);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    if (!customValue) {
      setCustomValue('60');
      onChange(60);
    }
  };

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Allow empty or valid positive numbers
    if (newValue === '' || /^\d+$/.test(newValue)) {
      setCustomValue(newValue);
      
      // Validate and update parent
      const durationNum = parseInt(newValue, 10);
      if (!isNaN(durationNum) && durationNum > 0) {
        onChange(durationNum);
      } else if (newValue === '') {
        onChange(undefined);
      }
    }
  };

  const handleCustomBlur = () => {
    // Ensure valid value on blur
    if (customValue) {
      const durationNum = parseInt(customValue, 10);
      if (isNaN(durationNum) || durationNum <= 0) {
        setCustomValue('60');
        onChange(60);
      }
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div className="space-y-3">
        {/* Preset duration buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PRESET_DURATIONS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePresetClick(preset.value)}
              disabled={disabled}
              className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                !isCustom && value === preset.value
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {preset.label}
            </button>
          ))}
          
          {/* Custom duration button */}
          <button
            type="button"
            onClick={handleCustomClick}
            disabled={disabled}
            className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
              isCustom
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            Custom
          </button>
        </div>

        {/* Custom duration input */}
        {isCustom && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                inputMode="numeric"
                value={customValue}
                onChange={handleCustomChange}
                onBlur={handleCustomBlur}
                disabled={disabled}
                placeholder="60"
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
                  error ? 'border-red-500' : 'border-gray-300'
                } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              <span className="text-sm text-gray-600">minutes</span>
            </div>
            
            {customValue && parseInt(customValue, 10) > 0 && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Duration:</span> {formatDuration(parseInt(customValue, 10))}
              </div>
            )}
          </div>
        )}

        {/* Display formatted duration for presets */}
        {!isCustom && value !== undefined && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Duration:</span> {formatDuration(value)}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};
